import { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import { Input } from '../../components/ui/input';
import { MagnifyingGlass, ShieldCheck, ShieldWarning, XCircle, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

export function AccessLogSection() {
  const [data, setData] = useState({ records: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [onlyFailed, setOnlyFailed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getAccessLog({ limit: 500, search: search || undefined, only_failed: onlyFailed });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore caricamento log accessi');
    } finally { setLoading(false); }
  }, [search, onlyFailed]);

  useEffect(() => { const t = setTimeout(() => load(), 300); return () => clearTimeout(t); }, [load]);

  const parseBrowser = (ua) => {
    if (!ua) return '-';
    if (/Edg\//i.test(ua)) return 'Edge';
    if (/Chrome\//i.test(ua) && !/Edg\//i.test(ua)) return 'Chrome';
    if (/Firefox\//i.test(ua)) return 'Firefox';
    if (/Safari\//i.test(ua) && !/Chrome/i.test(ua)) return 'Safari';
    return 'Altro';
  };
  const parseOs = (ua) => {
    if (!ua) return '';
    if (/Windows/i.test(ua)) return 'Windows';
    if (/Mac OS/i.test(ua)) return 'macOS';
    if (/Android/i.test(ua)) return 'Android';
    if (/iPhone|iPad|iOS/i.test(ua)) return 'iOS';
    if (/Linux/i.test(ua)) return 'Linux';
    return '';
  };

  return (
    <>
      <div className="mb-6 animate-fade-in">
        <h1 className="text-2xl sm:text-3xl font-bold font-['Work_Sans'] tracking-tight mb-2">Log Accessi</h1>
        <p className="text-[#52525B] text-sm">Storico degli accessi al sistema (login riusciti e falliti). Retention: 6 mesi.</p>
      </div>
      <div className="flex flex-wrap items-center gap-3 mb-4 animate-fade-in animate-delay-1">
        <div className="relative w-full sm:w-[300px]">
          <MagnifyingGlass size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
          <Input type="text" placeholder="Cerca per utente, username, IP..." value={search} onChange={e => setSearch(e.target.value)} className="rounded-sm h-9 text-sm pl-8" data-testid="access-log-search" />
        </div>
        <button
          onClick={() => setOnlyFailed(v => !v)}
          className={`flex items-center gap-1.5 rounded-sm h-9 px-3 text-sm font-semibold transition-colors ${onlyFailed ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-white border border-[#E4E4E7] text-[#52525B] hover:bg-[#FAFAFA]'}`}
          data-testid="filter-failed-btn"
        >
          <ShieldWarning size={16} weight={onlyFailed ? 'fill' : 'regular'} />
          {onlyFailed ? 'Solo Falliti' : 'Tutti'}
        </button>
        <div className="text-sm text-[#52525B] font-medium ml-auto" data-testid="access-log-count">{data.total} accessi</div>
      </div>
      <div className="animate-fade-in animate-delay-2 overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0">
        {loading ? <div className="text-center py-12 text-[#52525B]">Caricamento...</div> : data.records.length === 0 ? (
          <div className="text-center py-12 bg-white border border-[#E4E4E7] rounded-sm">
            <ShieldCheck size={48} className="mx-auto text-[#E4E4E7] mb-4" />
            <p className="text-[#52525B]">Nessun accesso trovato</p>
          </div>
        ) : (
          <div className="bg-white border border-[#E4E4E7] rounded-sm overflow-hidden">
            <table className="w-full" data-testid="access-log-table">
              <thead><tr className="border-b border-[#E4E4E7] bg-[#FAFAFA]">
                <th className="text-center px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B] w-10">✓</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Data/Ora</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Utente</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Ruolo</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">IP</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Browser</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Motivo</th>
              </tr></thead>
              <tbody>{data.records.map(l => {
                const ts = new Date(l.timestamp);
                const roleLabel = l.role ? l.role.charAt(0).toUpperCase() + l.role.slice(1) : '-';
                return (<tr key={l.id} className={`border-b border-[#E4E4E7] ${l.success ? 'hover:bg-[#FAFAFA]' : 'bg-red-50/50 hover:bg-red-50'}`}>
                  <td className="px-3 py-1.5 text-center">
                    {l.success
                      ? <CheckCircle size={18} className="inline text-green-600" weight="fill" />
                      : <XCircle size={18} className="inline text-red-600" weight="fill" />}
                  </td>
                  <td className="px-3 py-1.5 text-xs whitespace-nowrap"><div>{ts.toLocaleDateString('it-IT')}</div><div className="text-[#A1A1AA]">{ts.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</div></td>
                  <td className="px-3 py-1.5 text-sm font-medium uppercase">{l.user_name || l.username_tried || '-'}</td>
                  <td className="px-3 py-1.5 text-xs"><span className={`px-2 py-0.5 rounded font-semibold ${l.role === 'admin' ? 'bg-indigo-100 text-indigo-700' : l.role ? 'bg-gray-100 text-gray-700' : 'bg-red-100 text-red-700'}`}>{roleLabel}</span></td>
                  <td className="px-3 py-1.5 text-xs font-mono text-[#52525B]">{l.ip || '-'}</td>
                  <td className="px-3 py-1.5 text-xs text-[#52525B]">
                    <div>{parseBrowser(l.user_agent)}</div>
                    <div className="text-[#A1A1AA]">{parseOs(l.user_agent)}</div>
                  </td>
                  <td className="px-3 py-1.5 text-xs">
                    {l.success
                      ? <span className="text-green-700">Login OK</span>
                      : <span className="text-red-700 font-medium">{l.reason === 'user_not_found' ? 'Utente inesistente' : l.reason === 'wrong_password' ? 'Password errata' : l.reason || 'Fallito'}</span>}
                  </td>
                </tr>);
              })}</tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
